import * as v from 'valibot';

/**
 * Control concepts a panel exposes — not how they are drawn or felt
 * under the hand.
 *
 * Each panel declares which concepts it implements and which primitive
 * instances back each concept. The same `Iris` concept manifests as the
 * vertical throw of a shading joystick on RCP-3500/OCP-400/CGP-500 and as
 * a dedicated rotary with digital readout on MCP-400/MSU-3000 — concept
 * identical, physical binding differs. Active shading panels (RCP, OCP,
 * CGP) use the joystick; supervisory panels (MCP, MSU) use a rotary
 * because the operator does not work iris actively from a supervisory
 * surface.
 *
 * Pedestal consumes this layer. The contract surface MUST mirror what
 * pedestal can route across vendors; coordinate with the user before
 * adding or removing concepts.
 *
 * See bootstrap brief §11 and ADR-0007 (Contract surface),
 * ADR-0012 (Brief anatomy corrections).
 */
export const Concept = {
  // --- Paint and exposure (continuous parameters) -----------------------
  Iris: 'iris',
  Detail: 'detail',
  MultiMatrix: 'multi-matrix',
  Gamma: 'gamma',
  Knee: 'knee',
  MasterBlack: 'master-black',
  GainStep: 'gain-step',
  ColourTemperature: 'colour-temperature',
  WhiteBalance: 'white-balance',
  BlackBalance: 'black-balance',
  Shutter: 'shutter',
  NdFilter: 'nd-filter',
  ColourMatrix: 'colour-matrix',

  // RGB sub-parameters (CGP-500 paint rotaries, RCP-3500 paint section)
  GainRed: 'gain-red',
  GainGreen: 'gain-green',
  GainBlue: 'gain-blue',
  BlackRed: 'black-red',
  BlackGreen: 'black-green',
  BlackBlue: 'black-blue',
  FlareRed: 'flare-red',
  FlareGreen: 'flare-green',
  FlareBlue: 'flare-blue',

  // --- PTZ domain (Phase 3 onwards) ------------------------------------
  Pan: 'pan',
  Tilt: 'tilt',
  Zoom: 'zoom',
  Focus: 'focus',
  Preset: 'preset',

  // --- Trigger actions (one-shot events) -------------------------------
  // A trigger and a parameter are distinct shapes; pedestal routes them
  // through different code paths. Mapping a joystick axial press to
  // Preview must be type-equivalent to mapping the Preview push-button
  // to Preview — that is the entire point of the concept layer.
  Preview: 'preview',
  Program: 'program',
  Auto: 'auto',
  Call: 'call',
  Store: 'store',

  // --- State / mode toggles (latching) ---------------------------------
  Hold: 'hold',
  Free: 'free',
  DisplayModeSingle: 'display-mode-single',
  DisplayModeMulti: 'display-mode-multi',
  DisplayModeMatrix: 'display-mode-matrix',

  // --- Selection (which target this panel addresses) -------------------
  CameraSelect: 'camera-select',
  SceneSelect: 'scene-select',
} as const satisfies Record<string, string>;

export type Concept = (typeof Concept)[keyof typeof Concept];

export const ConceptSchema = v.picklist(Object.values(Concept));

/**
 * Concepts that appear in `inventory.json` `function.concept` but are
 * NOT routable through pedestal. Keeping them off `Concept` is deliberate:
 * pedestal must only see things it can act on, while the inventory must
 * still describe everything physically present on the panel.
 *
 * Three families:
 *
 *   - Chassis non-interactive features (mounting holes, engraved labels,
 *     scene-card slots, indicator lamps, tally outputs)
 *   - Mode / navigation buttons whose only effect is to drive the panel's
 *     own menu state machine (BARS, PANEL LOCK, FILES, EXIT, NEXT, …)
 *   - Menu surface items — each "menu-*" concept is a function reachable
 *     through a routable menu primitive, not separately routable itself
 *
 * Coordinate with the user before promoting an inventory-only concept
 * to a first-class `Concept`. The boundary is "can pedestal route it
 * across vendors", not "does the operator press it".
 */
export const InventoryOnlyConcept = {
  // Chassis non-interactive
  Mounting: 'mounting',
  EngravedLabel: 'engraved-label',
  SceneCardSlot: 'scene-card-slot',
  Tally: 'tally',
  IrisReadout: 'iris-readout',
  LensExtenderIndicator: 'lens-extender-indicator',
  MenuDisplay: 'menu-display',

  // Mode / navigation
  Assignable: 'assignable',
  Bars: 'bars',
  PanelLock: 'panel-lock',
  RecallStandard: 'recall-standard',
  FilesMenu: 'files-menu',
  SetupMenu: 'setup-menu',
  ExitMenu: 'exit-menu',
  MenuToggle: 'menu-toggle',
  MenuSelect: 'menu-select',
  NavigatePrev: 'navigate-prev',
  NavigateNext: 'navigate-next',

  // Menu surface items (functions reachable through a menu primitive)
  MenuBlack: 'menu-black',
  MenuColorBalance: 'menu-color-balance',
  MenuDetail: 'menu-detail',
  MenuExposureTime: 'menu-exposure-time',
  MenuFilters: 'menu-filters',
  MenuFlare: 'menu-flare',
  MenuGain: 'menu-gain',
  MenuGamma: 'menu-gamma',
  MenuKnee: 'menu-knee',
  MenuSkin: 'menu-skin',
} as const satisfies Record<string, string>;

export type InventoryOnlyConcept = (typeof InventoryOnlyConcept)[keyof typeof InventoryOnlyConcept];

export const InventoryOnlyConceptSchema = v.picklist(Object.values(InventoryOnlyConcept));

/**
 * Concept surface accepted by `inventory.json` — the routable `Concept`
 * set plus the panel-internal `InventoryOnlyConcept` set. Use this in
 * the inventory schema; use `Concept` everywhere else.
 */
export const InventoryConceptSchema = v.union([ConceptSchema, InventoryOnlyConceptSchema]);
export type InventoryConcept = Concept | InventoryOnlyConcept;

/**
 * Concept domain grouping. Used by tooling and pedestal to dispatch
 * concept events through the correct routing code path — continuous
 * parameter writes are not the same operation as one-shot triggers.
 */
export const ConceptDomain = {
  Paint: 'paint',
  Exposure: 'exposure',
  ColourScience: 'colour-science',
  Ptz: 'ptz',
  TriggerAction: 'trigger-action',
  ModeToggle: 'mode-toggle',
  Selection: 'selection',
} as const satisfies Record<string, string>;

export type ConceptDomain = (typeof ConceptDomain)[keyof typeof ConceptDomain];

export const conceptDomain: Record<Concept, ConceptDomain> = {
  // Paint / exposure / colour science — continuous parameters
  [Concept.Iris]: ConceptDomain.Exposure,
  [Concept.Detail]: ConceptDomain.Paint,
  [Concept.MultiMatrix]: ConceptDomain.ColourScience,
  [Concept.Gamma]: ConceptDomain.Paint,
  [Concept.Knee]: ConceptDomain.Paint,
  [Concept.MasterBlack]: ConceptDomain.Paint,
  [Concept.GainStep]: ConceptDomain.Exposure,
  [Concept.ColourTemperature]: ConceptDomain.ColourScience,
  [Concept.WhiteBalance]: ConceptDomain.ColourScience,
  [Concept.BlackBalance]: ConceptDomain.ColourScience,
  [Concept.Shutter]: ConceptDomain.Exposure,
  [Concept.NdFilter]: ConceptDomain.Exposure,
  [Concept.ColourMatrix]: ConceptDomain.ColourScience,
  [Concept.GainRed]: ConceptDomain.Paint,
  [Concept.GainGreen]: ConceptDomain.Paint,
  [Concept.GainBlue]: ConceptDomain.Paint,
  [Concept.BlackRed]: ConceptDomain.Paint,
  [Concept.BlackGreen]: ConceptDomain.Paint,
  [Concept.BlackBlue]: ConceptDomain.Paint,
  [Concept.FlareRed]: ConceptDomain.Paint,
  [Concept.FlareGreen]: ConceptDomain.Paint,
  [Concept.FlareBlue]: ConceptDomain.Paint,

  // PTZ — continuous parameters
  [Concept.Pan]: ConceptDomain.Ptz,
  [Concept.Tilt]: ConceptDomain.Ptz,
  [Concept.Zoom]: ConceptDomain.Ptz,
  [Concept.Focus]: ConceptDomain.Ptz,
  [Concept.Preset]: ConceptDomain.Ptz,

  // Triggers — one-shot
  [Concept.Preview]: ConceptDomain.TriggerAction,
  [Concept.Program]: ConceptDomain.TriggerAction,
  [Concept.Auto]: ConceptDomain.TriggerAction,
  [Concept.Call]: ConceptDomain.TriggerAction,
  [Concept.Store]: ConceptDomain.TriggerAction,

  // Mode toggles — latching state
  [Concept.Hold]: ConceptDomain.ModeToggle,
  [Concept.Free]: ConceptDomain.ModeToggle,
  [Concept.DisplayModeSingle]: ConceptDomain.ModeToggle,
  [Concept.DisplayModeMulti]: ConceptDomain.ModeToggle,
  [Concept.DisplayModeMatrix]: ConceptDomain.ModeToggle,

  // Selection — addresses the target this panel acts upon
  [Concept.CameraSelect]: ConceptDomain.Selection,
  [Concept.SceneSelect]: ConceptDomain.Selection,
};
